/**
 * NudgeStrip — structural placeholder for contextual prompt suggestions.
 *
 * Phase 1b: ships hidden (hidden=true default). Phase 4+ will flip it on
 * when real nudge data is available from the backend.
 *
 * Mounted inside Composer, below the textarea and above the chat footer.
 */

interface NudgeStripProps {
	/** When true (default), renders null — no DOM output. Phase 4 flips this. */
	hidden?: boolean;
	nudges?: Array<{
		id: string;
		icon: "warn" | "mail" | "clock" | "bell";
		tone?: "warn" | "info";
		label: string;
		onClick?: () => void;
	}>;
}

/**
 * Structural placeholder for the nudge strip.
 * Returns null in Phase 1b (hidden=true).
 * When enabled, renders an empty container that CSS collapses via :empty rule.
 */
export default function NudgeStrip({ hidden = true, nudges = [] }: NudgeStripProps) {
	if (hidden) return null;

	if (nudges.length === 0) {
		// Empty container — CSS :empty { display: none } guard collapses it
		return <div className="composer-nudge-strip" />;
	}

	return (
		<div className="composer-nudge-strip" role="status" aria-label="Suggested actions">
			{nudges.map((nudge) => (
				<button
					key={nudge.id}
					type="button"
					className={`nudge-strip-item nudge-strip-item--${nudge.tone ?? "info"}`}
					onClick={nudge.onClick}
				>
					<span className="nudge-strip-icon" aria-hidden="true">{nudge.icon}</span>
					<span className="nudge-strip-label">{nudge.label}</span>
				</button>
			))}
		</div>
	);
}
